import { createAsyncThunk, createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { getEmassarData } from "../api";
import { EmassarMethod } from "../../types/emassar-method";
import { generateDictionaries } from "../../utils/conversion-directory-arkgis";
import {RootState} from "../store";
import {LayersDataTypes} from "../../types/layers-data-types";
import {Layer} from "../../interfaces/layer";
import { GetFields, GetQueryFeatures } from "../../utils/feature-service";
import { LayerType } from "../../types/layer-type";

export interface Company {
    tenant_id: number;
    name_en: string;
    name_ar: string;
    date_of_establish: string;
    phone: string;
    other_phones: string;
    mobile: string;
    email: string;
    website: string;
    estateEn: string;
    estateId: number;
    branches: Array<string>;
    prodtype: Array<string>;
}

interface CompaniesState {
    tenantIds: number[];
    tenantData: Company[];
    loading: boolean;
    error: string | null;
    searchQuery: string;
    cities: Map<number, string>;
    selectedCities: number[];
}

const initialState: CompaniesState = {
    tenantIds: [],
    tenantData: [],
    loading: false,
    error: null,
    searchQuery: "",
    cities: new Map<number, string>(),
    selectedCities: [],
};

const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${month}.${day}.${year}`;
};

export const fetchCompaniesData = createAsyncThunk(
    "companies/fetchCompaniesData",
    async (_, { getState, rejectWithValue }) => {
        try {
            const state: any = getState();
            const layers = state.map.layers.filter((layer: Layer) =>
                [LayersDataTypes.Plots, LayersDataTypes.InvestmentOpportunities, LayersDataTypes.SpacesForRent].includes(layer.dataType) && layer.type === LayerType.ArcgisFeatureServer
            );
            const tenantIdSet = new Set<number>();
            const estateEnMap = new Map<number, {estateEn: string, estateId: number}>();
            const prodtypeEMap = new Map<number, string[]>();
            let dictionaries: { [key: string]: { [key: string]: string } } = {};

            const dictionaryLayers = layers.filter((layer: Layer) =>
                [LayersDataTypes.Plots, LayersDataTypes.InvestmentOpportunities].includes(layer.dataType) && layer.type === LayerType.ArcgisFeatureServer
            );

            for (const layer of dictionaryLayers) {
                const fields = await GetFields(layer);
                if (fields) {
                    dictionaries = { ...dictionaries, ...generateDictionaries(fields) };
                }
            }

            for (const layer of layers) {
                const queryFeatures = await GetQueryFeatures(layer, ['estateID']);
                queryFeatures.features.forEach((feature: any) => {
                    const tenantId = feature.attributes.tenant_id;
                    let estateEn = feature.attributes.estateEn;

                    if (layer.dataType === LayersDataTypes.SpacesForRent && dictionaries["estateEn"] && feature.attributes.estateid) {
                        estateEn = dictionaries["estateEn"][feature.attributes.estateid];
                    }
                    let prodtype_e = feature.attributes.prodtype_e;
                    const estateId = layer.dataType === LayersDataTypes.SpacesForRent ? feature.attributes.estateid : feature.attributes.estateID

                    if (tenantId && tenantId !== 0) {
                        tenantIdSet.add(tenantId);

                        if (estateEn) {
                            estateEnMap.set(tenantId, {estateEn, estateId});
                        }

                        if (prodtype_e) {
                            prodtypeEMap.set(tenantId, [...prodtypeEMap.get(tenantId) ?? [], prodtype_e])
                        }
                    }
                });
            }
            const tenantIds = Array.from(tenantIdSet);

            const tenantData: Company[] =
                (await GetCompanies(tenantIds, estateEnMap)) || [];

            const resultTenantData = tenantData.map((tenant) => ({
                ...tenant,
                prodtype: Array.from(prodtypeEMap.get(tenant.tenant_id) || []).filter((e, i, self) => e && i === self.indexOf(e)).sort(),
            }));

            return { tenantIds, tenantData: resultTenantData, cities: new Map(Object.entries(dictionaries["estateEn"]).map(([key, value])=> [+key, value])) };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to load companies data");
        }
    },
);
export const companiesSlice = createSlice({
    name: "companies",
    initialState,
    reducers: {
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;
        },
        setSelectedCities: (state, action: PayloadAction<number[]>) => {
            state.selectedCities = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCompaniesData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                fetchCompaniesData.fulfilled,
                (
                    state,
                    action: PayloadAction<{ tenantIds: number[]; tenantData: Company[]; cities: Map<number, string>; }>,
                ) => {
                    state.tenantIds = action.payload.tenantIds;
                    state.tenantData = action.payload.tenantData.sort((a, b) =>
                        a.name_en.localeCompare(b.name_en),
                    );
                    state.cities = action.payload.cities;
                    state.loading = false;
                },
            )
            .addCase(fetchCompaniesData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setSearchQuery, setSelectedCities } = companiesSlice.actions;

export default companiesSlice.reducer;

const selectCompanies = (state: RootState) => state.companies;

export const selectFilteredTenantData = createSelector(
    [selectCompanies],
    (companies) => {
        const { tenantData, searchQuery, selectedCities } = companies;
        let filtered = tenantData;

        if (searchQuery.trim()) {
            const query = searchQuery.trim().toLowerCase();
                filtered = filtered.filter((tenant) =>
                    tenant.name_en.toLowerCase().includes(query)
                );
        }

        if (selectedCities && selectedCities.length) {
            filtered = filtered.filter(
                (tenant) => selectedCities.includes(tenant.estateId)
            );
        }

        return filtered;
    }
);

export const GetCompanies = async (tenantIds: number[], estateEnMap: Map<number, {estateEn: string, estateId: number}>): Promise<Company[]> => {
    const tenantData: Company[] = (await getEmassarData(EmassarMethod.Tenant, tenantIds)) || [];

    const groupedTenants = tenantData.reduce((acc, tenant) => {
        const key = tenant.name_en;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(tenant.tenant_id);
        return acc;
    }, {} as Record<string, number[]>);
    const branchesMap = new Map<string, Set<string>>();

    Object.entries(groupedTenants).forEach(([companyName, tenantIds]) => {
        const uniqueBranches = new Set<string>();
        tenantIds.forEach((tenantId) => {
            const estate = estateEnMap.get(tenantId);
            if (estate?.estateEn) {
                uniqueBranches.add(estate.estateEn);
            }
        });
        branchesMap.set(companyName, uniqueBranches);
    });

    return tenantData.map((tenant) => ({
        ...tenant,
        date_of_establish: formatDate(tenant.date_of_establish),
        ...estateEnMap.get(tenant.tenant_id),
        branches: Array.from(branchesMap.get(tenant.name_en) || []).sort(),
    }));
}
